import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button } from '@components/ui';
import { stokYeterliMi } from '@utils/stok';

/**
 * StokAktarModal — İnşaatlar arası stok aktarım modalı
 * 
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {Function} onSubmit - ({kaynakInsaat, hedefInsaat, miktar, not}) => void
 * @param {string} malzemeTipi - 'demir' | 'parke'
 * @param {Array} stokData - Mevcut stok verisi
 * @param {string} birim - Birim (ton, kutu vb.)
 * @param {boolean} isLoading
 */
export default function StokAktarModal({ isOpen, onClose, onSubmit, malzemeTipi, stokData = [], birim = 'adet', isLoading = false }) {
    const { constructions } = useSelector((state) => state.construction);
    const [altTip, setAltTip] = useState('');
    const [hedefInsaat, setHedefInsaat] = useState('');
    const [miktar, setMiktar] = useState('');
    const [not, setNot] = useState('');

    const selectedStok = stokData.find(s => s.altTip === altTip);
    const serbestStok = selectedStok?.serbestStok || 0;
    const miktarNum = parseFloat(miktar) || 0;
    const yeterli = stokYeterliMi(serbestStok, miktarNum);

    const handleSubmit = () => {
        if (!altTip || !hedefInsaat || !yeterli) return;
        onSubmit({ altTip, hedefInsaat, miktar: miktarNum, not, malzemeTipi });
        setAltTip(''); setHedefInsaat(''); setMiktar(''); setNot('');
    };

    const inputClass = "w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50";

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Stok Aktarımı" size="md"
            footer={<><Button variant="ghost" onClick={onClose} disabled={isLoading}>İptal</Button><Button variant="primary" onClick={handleSubmit} isLoading={isLoading} disabled={!yeterli || !altTip || !hedefInsaat}>Aktarımı Onayla</Button></>}>
            <div className="space-y-4">
                {/* Alt Tip Seçimi */}
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">Malzeme Alt Tipi <span className="text-error">*</span></label>
                    <select value={altTip} onChange={(e) => setAltTip(e.target.value)} className={inputClass}>
                        <option value="">Seçiniz...</option>
                        {stokData.map(s => (
                            <option key={s.altTip} value={s.altTip}>{s.altTip} — Serbest: {s.serbestStok} {birim}</option>
                        ))}
                    </select>
                </div>

                {/* Serbest Stok Bilgisi */}
                {altTip && (
                    <div className="p-3 bg-bg-tertiary rounded-lg flex items-center justify-between">
                        <span className="text-sm text-text-secondary">Serbest Stok:</span>
                        <span className="text-lg font-bold text-success">{serbestStok} {birim}</span>
                    </div>
                )}

                {/* Hedef İnşaat */}
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">Hedef İnşaat <span className="text-error">*</span></label>
                    <select value={hedefInsaat} onChange={(e) => setHedefInsaat(e.target.value)} className={inputClass}>
                        <option value="">Seçiniz...</option>
                        {(constructions || []).map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {/* Miktar */}
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">Miktar ({birim}) <span className="text-error">*</span></label>
                    <input type="number" step="0.1" value={miktar} onChange={(e) => setMiktar(e.target.value)}
                        placeholder="0" className={`${inputClass} ${miktarNum > 0 && !yeterli ? 'border-error' : ''}`} />
                    {miktarNum > 0 && !yeterli && (
                        <p className="mt-1 text-xs text-error">Yetersiz stok! Serbest: {serbestStok} {birim}</p>
                    )}
                </div>

                {/* Not */}
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">Aktarım Notu</label>
                    <input type="text" value={not} onChange={(e) => setNot(e.target.value)} placeholder="Aktarım açıklaması..."
                        className={inputClass} />
                </div>
            </div>
        </Modal>
    );
}
